import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import React from "react";
import { BsInfoCircleFill } from "react-icons/bs";
import { MdHighlightOff } from "react-icons/md";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

/* ── Small card for displaying a labelled value ── */
const DetailCard = ({ label, value, isActive }: { label: string; value: string | number | undefined; isActive: any }) => (
  <div className="kyci-detail-card">
    <p className="kyci-detail-label">{label}</p>
    <p className="kyci-detail-value">{isActive ? (value || "—") : <span className="kyci-na">N/A</span>}</p>
  </div>
);

const InfoModal = ({ info, varient, type }: { info?: any; varient?: string; type: any }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);

  const handleClose = () => {
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  const safeJSON = (input: any, fallback: any = {}) => {
    try { return JSON.parse(input) ?? fallback; }
    catch { return fallback; }
  };

  const txLimit = safeJSON(info?.transactionLimit);
  const maxTx   = safeJSON(info?.maxTransactionAmount);
  const pm      = safeJSON(info?.paymentMethods, {});
  const activePM = Object.entries(pm).filter(([, v]) => v === true).map(([k]) => k);

  return (
    <>
      <Popover trigger="hover" placement="bottom">
        <PopoverTrigger>
          <button className="kyci-trigger" onClick={() => onOpen()} disabled={!info} aria-label="View velocity">
            <BsInfoCircleFill size={varient === "small" ? 14 : 15} />
            <span>View</span>
          </button>
        </PopoverTrigger>
        <PopoverContent border="1px solid #e9d5ff" borderRadius="12px" bg="#fdf4ff" maxW="200px" p="0" overflow="hidden" zIndex="20">
          <PopoverHeader bg="#fae8ff" fontSize="12px" fontWeight="700" color="#6d28d9" borderBottom="1px solid #e9d5ff" px="12px" py="9px">View Velocity</PopoverHeader>
          <PopoverArrow bg="#fdf4ff" />
        </PopoverContent>
      </Popover>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`kyci-panel${isOpen && !isCloseModal ? " kyci-panel--open" : " kyci-panel--close"}`}>
          <div className="kyci-glow" />

          <div className="kyci-header">
            <div className="kyci-header-brand">
              <div className="kyci-header-logo">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  <path d="M16 11l2 2 4-4" strokeWidth="2.5"/>
                </svg>
              </div>
              <div>
                <h2 className="kyci-title">KYC Velocity Data</h2>
                <p className="kyci-sub">{info?.country || "Velocity configuration"}</p>
              </div>
            </div>
            <button className="kyci-close" onClick={handleClose}><MdHighlightOff size={22} /></button>
          </div>

          <div className="kyci-body">
            {/* General */}
            <div className="kyci-section">
              <h3 className="kyci-section-title">
                <span className="kyci-section-dot kyci-section-dot--purple" />
                General Info
              </h3>
              <div className="kyci-grid-2">
                <DetailCard label="Country" value={info?.country} isActive={true} />
              </div>
            </div>

            {/* Payout Limits */}
            <div className="kyci-section">
              <h3 className="kyci-section-title">
                <span className="kyci-section-dot kyci-section-dot--blue" />
                Payout Limits
              </h3>
              <div className="kyci-grid-2">
                <DetailCard label="Transaction Limit" value={`${txLimit.limit_no ?? "—"} / ${txLimit.period ?? "—"}`} isActive={true} />
                <DetailCard label="Max Transaction Amount" value={`${maxTx.amount ?? "—"} ${maxTx.currency ?? ""} / ${maxTx.period ?? "—"}`} isActive={true} />
              </div>
            </div>

            {/* KYC Levels */}
            <div className="kyci-section">
              <h3 className="kyci-section-title">
                <span className="kyci-section-dot kyci-section-dot--green" />
                KYC Levels
              </h3>
              <div className="kyci-levels-grid">
                {([
                  { key: "noKyc", label: "No KYC", color: "green" },
                  { key: "sdd",   label: "SDD",    color: "amber" },
                  { key: "edd",   label: "EDD",    color: "red"   },
                ] as { key: string; label: string; color: "green" | "amber" | "red" }[]).map(({ key, label, color }) => {
                  const d = safeJSON(info?.[key]);
                  return (
                    <div key={key} className={`kyci-level-card kyci-level-card--${color}`}>
                      <div className="kyci-level-header">
                        <span className="kyci-level-name">{label}</span>
                        <span className={`kyci-level-badge${d.is_active ? " kyci-level-badge--on" : " kyci-level-badge--off"}`}>
                          {d.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="kyci-level-rows">
                        <div className="kyci-level-row">
                          <span className="kyci-level-key">Min Amount</span>
                          <span className="kyci-level-val">{d.is_active ? `${d.min_amount_per_transaction?.amount ?? "—"} ${d.min_amount_per_transaction?.currency ?? ""}` : "N/A"}</span>
                        </div>
                        <div className="kyci-level-row">
                          <span className="kyci-level-key">Max Amount</span>
                          <span className="kyci-level-val">{d.is_active ? `${d.max_amount_per_transaction?.amount ?? "—"} ${d.max_amount_per_transaction?.currency ?? ""}` : "N/A"}</span>
                        </div>
                        <div className="kyci-level-row">
                          <span className="kyci-level-key">KYC Count</span>
                          <span className="kyci-level-val">{d.is_active ? (d.kyc_count?.count ?? "—") : "N/A"}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="kyci-section">
              <h3 className="kyci-section-title">
                <span className="kyci-section-dot kyci-section-dot--purple" />
                Payment Methods
              </h3>
              {activePM.length > 0 ? (
                <div className="kyci-pm-tags">
                  {activePM.map((k) => (
                    <span key={k} className="kyci-pm-tag">{k.replace("_", " ")}</span>
                  ))}
                </div>
              ) : (
                <p className="kyci-no-pm">No active payment methods</p>
              )}
            </div>
          </div>

          <div className="kyci-footer">
            <span className="kyci-footer-hint">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Read-only view
            </span>
            <button className="kyci-btn-close" onClick={handleClose}>Close</button>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .kyci-trigger { display:inline-flex; align-items:center; gap:5px; font-family:'Plus Jakarta Sans',sans-serif; font-size:12px; font-weight:700; color:#7c3aed; padding:5px 10px; border-radius:8px; border:1.5px solid #e9d5ff; background:#fdf4ff; cursor:pointer; transition:all .15s; white-space:nowrap; }
        .kyci-trigger:hover:not(:disabled) { background:#fae8ff; border-color:#c4b5fd; }
        .kyci-trigger:disabled { opacity:.4; cursor:not-allowed; }

        .kyci-panel { font-family:'Plus Jakarta Sans',sans-serif; position:fixed; top:0; right:0; bottom:0; width:100%; max-width:660px; background:#fff; z-index:1004; display:flex; flex-direction:column; box-shadow:-12px 0 60px rgba(15,23,42,.18); transition:transform .35s cubic-bezier(0.16,1,0.3,1); overflow:hidden; }
        .kyci-panel--open  { transform:translateX(0); }
        .kyci-panel--close { transform:translateX(100%); }

        .kyci-glow { height:3px; flex-shrink:0; background:linear-gradient(90deg,#4c1d95,#7c3aed,#a855f7,#7c3aed,#4c1d95); background-size:200% 100%; animation:kyciGlow 3s linear infinite; }
        @keyframes kyciGlow { 0%{background-position:0%} 100%{background-position:200%} }

        .kyci-header { padding:15px 24px; background:linear-gradient(135deg,#4c1d95 0%,#6d28d9 60%,#7c3aed 100%); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .kyci-header-brand { display:flex; align-items:center; gap:13px; }
        .kyci-header-logo { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .kyci-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
        .kyci-sub { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; }
        .kyci-close { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; transition:color .15s; }
        .kyci-close:hover { color:#fff; }

        .kyci-body { flex:1; overflow-y:auto; padding:22px 24px; display:flex; flex-direction:column; gap:20px; }
        .kyci-body::-webkit-scrollbar { width:4px; }
        .kyci-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }

        .kyci-section { display:flex; flex-direction:column; gap:10px; }
        .kyci-section-title { display:flex; align-items:center; gap:8px; font-size:13px; font-weight:800; color:#0f172a; }
        .kyci-section-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
        .kyci-section-dot--purple { background:#7c3aed; }
        .kyci-section-dot--blue   { background:#2563eb; }
        .kyci-section-dot--green  { background:#16a34a; }

        .kyci-grid-2 { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:10px; }
        .kyci-detail-card { background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:11px; padding:12px 14px; }
        .kyci-detail-label { font-size:10.5px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:5px; }
        .kyci-detail-value { font-size:13.5px; font-weight:700; color:#0f172a; }
        .kyci-na { color:#cbd5e1; font-weight:500; font-style:italic; }

        .kyci-levels-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:10px; }
        .kyci-level-card { border-radius:12px; overflow:hidden; border:1.5px solid; }
        .kyci-level-card--green { border-color:#bbf7d0; background:linear-gradient(135deg,#f0fdf4,#dcfce7); }
        .kyci-level-card--amber { border-color:#fde68a; background:linear-gradient(135deg,#fffbeb,#fef3c7); }
        .kyci-level-card--red   { border-color:#fecdd3; background:linear-gradient(135deg,#fff1f2,#ffe4e6); }
        .kyci-level-header { display:flex; align-items:center; justify-content:space-between; padding:10px 13px 8px; border-bottom:1px solid rgba(0,0,0,0.05); }
        .kyci-level-name { font-size:12.5px; font-weight:800; color:#0f172a; }
        .kyci-level-badge { font-size:10px; font-weight:700; padding:2px 8px; border-radius:6px; }
        .kyci-level-badge--on  { background:#dcfce7; border:1px solid #bbf7d0; color:#15803d; }
        .kyci-level-badge--off { background:#f1f5f9; border:1px solid #e2e8f0; color:#94a3b8; }
        .kyci-level-rows { padding:10px 13px; display:flex; flex-direction:column; gap:7px; }
        .kyci-level-row { display:flex; align-items:center; justify-content:space-between; }
        .kyci-level-key { font-size:11px; font-weight:600; color:#94a3b8; }
        .kyci-level-val { font-size:12px; font-weight:700; color:#0f172a; }

        .kyci-pm-tags { display:flex; flex-wrap:wrap; gap:7px; }
        .kyci-pm-tag { padding:5px 12px; border-radius:8px; font-size:12px; font-weight:700; background:linear-gradient(135deg,#fdf4ff,#fae8ff); border:1.5px solid #e9d5ff; color:#7c3aed; text-transform:capitalize; }
        .kyci-no-pm { font-size:13px; color:#94a3b8; font-style:italic; }

        .kyci-footer { padding:12px 24px; border-top:1px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .kyci-footer-hint { display:flex; align-items:center; gap:5px; font-size:12px; color:#94a3b8; font-weight:500; }
        .kyci-btn-close { font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:8px 18px; border-radius:9px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
        .kyci-btn-close:hover { background:#f1f5f9; }

        @media (max-width:640px) { .kyci-panel { max-width:100%; } .kyci-levels-grid { grid-template-columns:1fr; } }
      `}</style>
    </>
  );
};

export default InfoModal;